/**
 * Login — the single sign-on gate.
 *
 * The hosted demo has no SSO, but the gate is real: the client must enter the
 * credential we sent them. There is no auto-fill, no persona shortcut buttons,
 * and no password bundled into the JS — so visiting the URL without the right
 * credential cannot get you in. On success you land on the all-access admin
 * view; the upper-left role selector then switches the view to any role.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { beginSsoLogin, ssoEnabled } from '../lib/sso';
import { ApiError } from '../lib/api';

export function Login(): ReactNode {
  const { isAuthenticated, login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The in-app password form only exists for demo builds (its /auth/login
  // backend is demo-mode only). With SSO live, "internal access" routes pool
  // accounts through the hosted UI's password form instead.
  const showInternal = !ssoEnabled();

  // Already signed in (e.g. a stored token rehydrated): go to the app.
  if (isAuthenticated) {
    return <Navigate to="/chat" replace />;
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (pending) return;
    if (!email.trim() || !password) {
      setError('Enter your email and password to sign in.');
      return;
    }
    setError(null);
    setPending(true);
    try {
      await login(email.trim(), password);
      // On success the AuthProvider sets isAuthenticated; the redirect renders.
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 401
          ? 'Invalid email or password.'
          : 'Sign-in failed. Please try again.',
      );
      setPending(false);
    }
  }

  return (
    <div className="min-h-full flex items-center justify-center bg-navy-900 p-6">
      <div className="card w-full max-w-sm">
        <div className="text-center">
          <img
            src="/trident-logo-black.png"
            alt="Trident"
            className="mx-auto h-12 w-auto"
          />
          <p className="mt-2 text-sm text-steel-600">Trident AI</p>
          {showInternal && (
            <p className="mt-4 text-sm text-steel-500">
              Sign in with the credentials provided to you.
            </p>
          )}
        </div>

        {error && (
          <div
            role="alert"
            className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
          >
            {error}
          </div>
        )}

        {ssoEnabled() && (
          <div className="mb-6">
            <button
              type="button"
              onClick={() => void beginSsoLogin()}
              className="w-full rounded-md bg-sky-600 px-4 py-2 font-medium text-white hover:bg-sky-700"
            >
              Sign in with your corporate ID
            </button>
            <div className="mt-4 text-center">
              <button
                type="button"
                data-testid="internal-access"
                onClick={() => void beginSsoLogin({ internal: true })}
                className="text-xs text-slate-400 underline-offset-2 hover:text-slate-600 hover:underline"
              >
                internal access
              </button>
            </div>
          </div>
        )}
        {showInternal && (
        <form className="mt-5 flex flex-col gap-3" onSubmit={onSubmit}>
          <label className="flex flex-col gap-1 text-left">
            <span className="text-xs font-medium text-steel-600">Email</span>
            <input
              type="email"
              name="email"
              autoComplete="username"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={pending}
              className="rounded-lg border border-steel-200 px-3 py-2 text-sm text-navy-900 focus:border-navy-400 focus:outline-none focus:ring-2 focus:ring-navy-500 disabled:opacity-50"
            />
          </label>
          <label className="flex flex-col gap-1 text-left">
            <span className="text-xs font-medium text-steel-600">Password</span>
            <input
              type="password"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={pending}
              className="rounded-lg border border-steel-200 px-3 py-2 text-sm text-navy-900 focus:border-navy-400 focus:outline-none focus:ring-2 focus:ring-navy-500 disabled:opacity-50"
            />
          </label>
          <button
            type="submit"
            data-testid="credentials-login"
            disabled={pending}
            className="mt-1 rounded-lg bg-navy-900 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-navy-800 disabled:opacity-50"
          >
            {pending ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
        )}
      </div>
    </div>
  );
}

export default Login;
